import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Edit, Trash2 } from 'lucide-react';

/**
 * Unified Resource Card Component
 * Based on the clean Roles card design
 * Used for: Team, Assets, Roles
 */

const ResourceCard = ({
  // Data
  id,
  name,
  icon: Icon,
  iconColor = '#3B82F6',
  
  // Primary badge (shown next to name)
  primaryBadge, // { label, color, variant, className }
  
  // Description/details text
  description,
  
  // Details lines (for Team: email, phone; for Assets: capacity)
  details = [], // [{ icon, text }] or just array of strings
  
  // Expandable content (See More section)
  expandedContent,
  showSeeMore = false,
  isExpanded = false, // Controlled expansion state
  onToggleExpand, // Controlled expansion handler
  
  // Actions
  onEdit,
  onDelete,
  editLabel = 'Edit',
  deleteLabel = 'Delete',
  
  // Mass actions
  massActionMode = false,
  isSelected = false,
  onToggleSelect,
  
  // Left border colors (array for multi-color)
  leftBorderColors = [],
  
  // Additional elements
  additionalBadges, // JSX for extra badges (e.g., Pending)
  additionalClassNames = '' // For special cases like opacity, ring colors
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  
  // Use controlled state if provided, otherwise use internal state
  const expanded = onToggleExpand !== undefined ? isExpanded : internalExpanded;
  const toggleExpand = onToggleExpand || (() => setInternalExpanded(!internalExpanded));

  return (
    <Card 
      className={`hover:shadow-md transition-shadow relative overflow-hidden ${isSelected ? 'ring-2 ring-blue-500' : ''} ${additionalClassNames}`}
    >
      {/* Single absolute div for all border colors (clipped by overflow-hidden) */}
      {leftBorderColors.length > 0 && (
        <div 
          className="absolute left-0 top-0 bottom-0 card-accent-border"
          style={{
            width: '4px',
            background: leftBorderColors.length === 1 
              ? leftBorderColors[0]
              : `linear-gradient(to bottom, ${leftBorderColors.map((color, idx) => {
                  const percent = 100 / leftBorderColors.length;
                  return `${color} ${idx * percent}%, ${color} ${(idx + 1) * percent}%`;
                }).join(', ')})`
          }}
        />
      )}
      
      {/* Checkbox - top right */}
      {massActionMode && (
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelect}
          className="absolute top-3 right-3 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer z-10"
        />
      )}
      
      <CardHeader className="pb-3">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div 
            className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ 
              backgroundColor: `${iconColor}20`, 
              border: `2px solid ${iconColor}` 
            }}
          >
            <Icon className="w-6 h-6" style={{ color: iconColor }} />
          </div>
          
          {/* Content - Name and Role ONLY */}
          <div className="flex-1 min-w-0">
            <CardTitle className="text-lg">{name}</CardTitle>
            
            {/* Primary Badge */}
            {primaryBadge && (
              <Badge 
                variant={primaryBadge.variant || "outline"}
                className={primaryBadge.className || "mt-1"}
                style={primaryBadge.color ? { 
                  borderColor: primaryBadge.color, 
                  color: primaryBadge.color 
                } : {}}
              >
                {primaryBadge.label}
              </Badge>
            )}
          </div>
        </div>
        
        {/* Details and Badges - FULL WIDTH BELOW */}
        <div className="mt-3 w-full">
          {/* Details (email, phone, etc.) */}
          {details.length > 0 && (
            <div className="text-xs text-gray-500 space-y-1">
              {details.map((detail, idx) => (
                <div key={idx} className="flex items-center gap-1.5">
                  {typeof detail === 'string' ? <span>{detail}</span> : (
                    <>
                      {detail.icon && <span className="flex-shrink-0">{detail.icon}</span>}
                      <span className="overflow-hidden text-ellipsis">{detail.text}</span>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Additional Badges */}
          {additionalBadges && (
            <div className="mt-2">
              {additionalBadges}
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Description - for Roles */}
        {description && (
          <p className="text-sm text-gray-600 mb-4 min-h-[40px]">
            {description}
          </p>
        )}
        
        {/* See More Toggle */}
        {showSeeMore && expandedContent && (
          <div className="mb-3">
            <button
              onClick={toggleExpand}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              {expanded ? 'See Less' : 'See More'}
            </button>
          </div>
        )}

        {/* Expanded Content */}
        {expanded && expandedContent && (
          <div className="space-y-3 mb-3 pt-3 border-t">
            {expandedContent}
          </div>
        )}

        {/* Action Buttons - EXACTLY like Roles card */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onEdit}
            className="flex-1"
          >
            <Edit className="w-3 h-3 mr-1" />
            {editLabel}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onDelete}
            className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-3 h-3 mr-1" />
            {deleteLabel}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ResourceCard;
